import find from 'lodash.find';
import get from 'lodash.get';
import notify from '@/plugins/notify';
import api from '../../helpers/api';
import { sortPositions } from '../../helpers/general';
import { IException } from '../../helpers/api-types';
import {
	IActionsState,
	IAction,
	ACTION_REMOVE,
	ACTION_RESUPPLY,
	ACTION_RECRUIT,
	IActionsResponse,
} from './Actions-types';
import { store } from '../../main';
import {
	DECREE_TYPE_ENLIST,
	DECREE_TYPE_REDEPLOY,
	DECREE_TYPE_REINFORCE,
} from '../DecreeCard/DecreeCard-types';
import { IGameStatus } from '../Game/Game-types';
import { COLOR_BLACK } from './Player-types';
import { UNIT_TYPE_ASSASSIN } from '../Unit/Unit-types';

function resolveFriendlyUnitId(actions: IAction[]) {
	const moveAction = find(actions, { type: 'move' });
	const attackAction = find(actions, { type: 'attack' });
	const controlAction = find(actions, { type: 'control' });
	const fallback = actions.find(action => action.friendlyUnitId);

	if (moveAction) {
		return moveAction.friendlyUnitId;
	} else if (attackAction) {
		return attackAction.friendlyUnitId;
	} else if (controlAction) {
		return controlAction.friendlyUnitId;
	} else if (fallback) {
		return fallback.friendlyUnitId;
	}

	return null;
}

const actionsState: IActionsState = {
	state: {
		actionModal: null,
		actions: [],
		activeDecree: null,
		activeDecreeId: null,
		bishopRecruit: true,
		decidingPlayerId: null,
		fortId: null,
		friendlyUnitId: null,
		hash: null,
		opponentUnitId: null,
		optionValue: null,
		lastActionTime: null,
		flipLongboat: false,
		removableUnitIds: [],
		resupplyUnitIds: [],
		selectableUnitIds: [],
		selectedPosition: null,
		selectedUnitActions: [],
		selectedUnitId: null,
		showTargetsModal: false,
		submitting: false,
	},
	mutations: {
		setActions(state, actions: IAction[]) {
			state.actions = actions;
		},
		resetSelections(state, all = true) {
			state.selectedUnitId = null;
			state.fortId = null;
			state.friendlyUnitId = null;
			state.opponentUnitId = null;
			state.selectedPosition = null;
			state.selectedUnitActions = [];
			state.actionModal = null;
			state.optionValue = null;
			state.selectableUnitIds = [];
			state.removableUnitIds = [];
			state.resupplyUnitIds = [];
			state.activeDecree = null;
			state.activeDecreeId = null;
			state.bishopRecruit = true;
			state.showTargetsModal = false;
		},
		setDecidingPlayerId(state, decidingPlayerId: number) {
			state.decidingPlayerId = decidingPlayerId;
		},
		setFlipLongboat(state, value: boolean) {
			state.flipLongboat = value;
		},
		setLastActionTime(state, lastActionTime: string) {
			state.lastActionTime = lastActionTime;
		},
		setFortId(state, fortId: number) {
			state.fortId = fortId;
		},
		setFriendlyUnitId(state, unitId: number) {
			state.friendlyUnitId = unitId;
		},
		setSelectedUnitId(state, unitId: number) {
			state.selectedUnitId = unitId;
		},
		setSelectedPosition(state, position: string) {
			state.selectedPosition = position;
		},
		setOpponentUnitId(state, unitId: number) {
			state.opponentUnitId = unitId;
		},
		setSelectedOptionValue(state, optionValue) {
			state.optionValue = optionValue;
		},
		setSelectedUnitActions(state, actions: IAction[]) {
			state.selectedUnitActions = actions;
		},
		setActionModal(state, action: IAction) {
			state.actionModal = action;
		},
		setRemovableUnitIds(state, removableUnitIds: number[]) {
			state.removableUnitIds = removableUnitIds;
		},
		setResupplyUnitIds(state, resupplyUnitIds: number[]) {
			state.resupplyUnitIds = resupplyUnitIds;
		},
		setSelectableUnitIds(state, selectableUnitIds: number[]) {
			state.selectableUnitIds = selectableUnitIds;
		},
		setSubmitting(state, value: boolean) {
			state.submitting = value;
		},
		setShowTargetsModal(state, value: boolean) {
			state.showTargetsModal = value;
		},
		setActiveDecree(state, activeDecree: string) {
			state.activeDecree = activeDecree;
		},
		setActiveDecreeId(state, activeDecreeId: number) {
			state.activeDecreeId = activeDecreeId;
		},
		setBishopRecruit(state, value: boolean) {
			state.bishopRecruit = value;
		},
		sethash(state, hash: string) {
			state.hash = hash;
		},
	},
	actions: {
		async sendCommand({ commit, state }, options) {
			const {
				$notify,
				actionType,
				gameId,
				playerId,
			} = options;

			const {
				actions,
				hash,
				bishopRecruit,
				fortId,
				friendlyUnitId,
				opponentUnitId,
				optionValue,
				selectedUnitId,
			} = state;

			const { unitsInHand } = store.state.gameState;

			let selectedPosition = state.selectedPosition;

			if (state.submitting) {
				return;
			}

			const tacticUnitId = actions.length ? actions[0].unitId : null;
			const secondaryActionId = actions.length ? actions[0].secondaryActionId : null;
			const selectedAction = find(actions, { type: actionType });
			const decreeId = selectedAction ? get(selectedAction, 'decreeId', null) : null;

			const selectedUnit = unitsInHand.find(unit => unit.id === selectedUnitId);

			if (selectedUnit?.type === UNIT_TYPE_ASSASSIN && ['attack', 'attack_fort', 'sacrifice_attack'].includes(actionType)) {
				selectedPosition = null;
			}

			const payload = {
				hash,
				actionType,
				decreeId,
				fortId,
				friendlyUnitId,
				gameId,
				opponentUnitId,
				optionValue,
				playerId,
				recruitType: bishopRecruit ? 'faceup' : 'facedown',
				secondaryActionId,
				targetPosition: selectedPosition,
				unitId: selectedUnitId ? selectedUnitId : tacticUnitId,
			};

			await commit('setSubmitting', true);

			try {
				const response = await api.request('/game/command', 'POST', payload);

				if (response.ok) {
					commit('resetSelections');
				} else {
					const error: IException = await response.json();

					$notify({
						group: 'app',
						title: 'Info',
						type: 'info',
						text: error ? error.message : 'Cannot perform action',
					});
				}
			} catch {
				await commit('setSubmitting', false);
			}

			await commit('setSubmitting', false);
		},
		setSelectedUnitId({ commit }, unitId: number) {
			commit('setSelectedUnitId', unitId);
		},
		setOpponentUnitId({ commit }, unitId: number) {
			commit('setOpponentUnitId', unitId);
		},
		setFortId({ commit }, fortId: number) {
			commit('setFortId', fortId);
		},
		setFriendlyUnitId({ commit }, unitId: number) {
			commit('setFriendlyUnitId', unitId);
		},
		resetSelections({ commit }) {
			commit('resetSelections');
		},
		setSelectedPosition({ commit }, targetPosition: string) {
			commit('setSelectedPosition', targetPosition);
		},
		setSelectedOptionValue({ commit }, optionValue: string) {
			commit('setSelectedOptionValue', optionValue);
		},
		setBishopRecruit({ commit }, options: { value: boolean, notify: boolean }) {
			if (options.notify) {
				notify({
					group: 'app',
					clean: true,
				});

				notify({
					group: 'app',
					text: `The Bishop's tactic is turned ${options.value ? 'on' : 'off'}`,
				});
			}

			commit('setBishopRecruit', options.value);
		},
		setShowTargetsModal({ commit }, value: boolean) {
			commit('setShowTargetsModal', value);
		},
		async getActions({ dispatch }, playerId: number) {
			const { isSpectator } = store.getters;

			if (isSpectator) {
				return;
			}

			const response = await api.request(`/player/${playerId}/actions`, 'GET');
			const parsedResponse: IActionsResponse = await response.json();

			dispatch('setActions', parsedResponse);
		},
		setActions({ commit, dispatch }, { actions, hash, decidingPlayerId, lastActionTime }) {
			const {
				getters: {
					player,
				},
				state: {
					gameState: {
						activePlayerId,
						state,
					},
				},
			} = store;

			commit('sethash', hash);
			commit('setDecidingPlayerId', decidingPlayerId);
			commit('setLastActionTime', lastActionTime);
			commit('setActions', actions);
			commit('setActionModal', null);
			commit('setActiveDecree', null);
			commit('setFlipLongboat', false);

			const firstAction = actions[0];

			notify({
				group: 'game',
				clean: true,
			});

			if (firstAction) {
				if (firstAction.secondaryActionId) {
					const { unitId } = firstAction;

					if (unitId) {
						const friendlyUnitAction = actions.filter(action => action.friendlyUnitId);
						const friendlyUnitId = friendlyUnitAction.length ?
							friendlyUnitAction[0].friendlyUnitId : unitId;
						dispatch('selectUnit', { unitId, friendlyUnitId });
					}

					if (firstAction.type === ACTION_REMOVE) {
						const removableUnitIds = actions.map(action => action.friendlyUnitId);
						commit('setRemovableUnitIds', removableUnitIds);
						commit('setActiveDecree', DECREE_TYPE_REDEPLOY);
					}

					if (firstAction.type === ACTION_RESUPPLY) {
						const resupplyUnitIds = actions.map(action => action.friendlyUnitId);
						commit('setResupplyUnitIds', resupplyUnitIds);
						commit('setActiveDecree', DECREE_TYPE_REINFORCE);
					}

					if (firstAction.type === ACTION_RECRUIT && firstAction.decreeId) {
						commit('setActiveDecree', DECREE_TYPE_ENLIST);
					}
					if (firstAction.decreeId) {
						commit('setActiveDecreeId', firstAction.decreeId);
					}
				}

				if (firstAction.options && firstAction.options.length) {
					commit('setActionModal', firstAction);
				}
			} else if (player?.id === activePlayerId && player?.id !== decidingPlayerId) {
				if (state !== IGameStatus.ENDED) {
					notify({
						group: 'game',
						title: 'Please wait...',
						text: 'Waiting for another player to make a choice.',
						duration: -1,
						data: {
							spinner: true,
						},
					});
				}
			}

			dispatch('setSelectableUnitIds');
		},
		setSelectableUnitIds({ state, commit }) {
			const { friendlyUnitIds, deployedUnitIds } = store.getters;
			const { actions, selectedUnitId } = state;
			const selectableUnitIds = [];

			actions.map((action) => {
				const { unitId, friendlyUnitId } = action;

				if (!selectableUnitIds.includes(unitId)) {
					selectableUnitIds.push(unitId);
				}

				if (selectedUnitId &&
					action.unitId === selectedUnitId &&
					friendlyUnitIds.includes(friendlyUnitId) &&
					!selectableUnitIds.includes(friendlyUnitId)) {
					selectableUnitIds.push(friendlyUnitId);
				}

				if (deployedUnitIds.includes(action.friendlyUnitId) &&
					!selectableUnitIds.includes(action.friendlyUnitId)) {
					selectableUnitIds.push(friendlyUnitId);
				}
			});

			commit('setSelectableUnitIds', selectableUnitIds);
		},
		selectUnit({ commit, state, dispatch }, options) {
			const { unitId, friendlyUnitId } = options;

			if (state.selectedUnitId === unitId) {
				commit('resetSelections');
				dispatch('setSelectableUnitIds');
				return;
			}

			const actions = state.actions.filter(action => action.unitId === unitId);
			const poisonActions = state.actions.filter(action => action.unitId === unitId && action.type === 'poison');

			if (actions.length) {
				commit('resetSelections');
				commit('setSelectedUnitId', unitId);
				commit('setSelectedUnitActions', actions);

				if (!friendlyUnitId) {
					const attackAction = find(actions, { type: 'attack' });
					const attackFortAction = find(actions, { type: 'attack_fort' });
					const friendlyUnitIdToSelect = resolveFriendlyUnitId(actions);

					if (friendlyUnitIdToSelect) {
						commit('setFriendlyUnitId', friendlyUnitIdToSelect);
					}

					if (attackFortAction && attackFortAction.targetPositions.length >= 2) {
						commit('setSelectedPosition', attackFortAction.targetPositions[0]);
					}

					if (attackAction && attackAction.targetPositions.length >= 2) {
						commit('setSelectedPosition', attackAction.targetPositions[0]);
					}

					if (poisonActions.length) {
						const {
							getters: {
								player: activePlayer,
							},
						} = store;

						const color = activePlayer?.team.color;

						const targetPositions = poisonActions.reduce((acc, action) => {
							if (action.targetPositions.length) {
								acc.push(...action.targetPositions);
							}
							return acc;
						}, []);

						const sortedTargetPositions = targetPositions.sort(sortPositions);

						if (color === COLOR_BLACK) {
							sortedTargetPositions.reverse();
						}

						if (sortedTargetPositions.length) {
							commit('setSelectedPosition', sortedTargetPositions[0]);
						}
					}
				}
			}

			if (friendlyUnitId) {
				commit('setFriendlyUnitId', friendlyUnitId);
			}

			dispatch('setSelectableUnitIds');
		},
		selectFriendlyUnitId({ commit }, friendlyUnitId) {
			commit('setFriendlyUnitId', friendlyUnitId);
		},
		setActiveDecree({ commit }, activeDecree: string) {
			commit('setActiveDecree', activeDecree);
		},
		setFlipLongboat({ commit }, value: boolean) {
			commit('setFlipLongboat', value);
		},
	},
};

export default actionsState;
